import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MatrixGroup,
  matrixConfigurationApi,
} from '@texas/api/endpoints/matrixConfigurationApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { MatrixGroupModal } from './MatrixGroupModal';

export function MatrixGroups() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<MatrixGroup | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    matrixConfigurationApi.getGroups,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const groups = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((group) => group.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.navigation.matrixGroups')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        canEdit ? (
          <Button
            variant="entityDirectoryPrimary"
            onClick={() => {
              setSelected(null);
              onOpen();
            }}
          >
            {t('configuration.newMatrixGroup')}
          </Button>
        ) : undefined
      }
    >
      <MatrixGroupModal
        canEdit={canEdit}
        group={selected}
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => refetch(currentUser.organizationId)}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && groups.length === 0 && (
              <Tr>
                <Td colSpan={2}>{t('general.noData')}</Td>
              </Tr>
            )}
            {groups.map((group) => (
              <Tr
                key={group.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(group);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(group);
                  onOpen();
                }}
              >
                <Td>{group.name}</Td>
                <Td>{group.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
